import { createTemplateByProgramById } from './template-service';
import { modalUtils } from '@/app/core/utils';
import { TermsAndConditions } from '@/app/features/products/common/components/TermsAndConditions';
import { useEffect, useState } from 'react';
import { Job } from '@ssar-app/domain';
import {
  Tooltip,
  Button,
  Checkbox,
  Textarea,
  FormControl,
  Grid,
  GridItem,
  FormLabel,
  FormHelperText,
  Input,
  Icon,
} from '@chakra-ui/react';
import { Modal, Spin } from '@ssar-app/uikit';
import { useHistory } from 'react-router-dom';
import { AiOutlineSnippets } from 'react-icons/ai/index.js';
import { usePathContext } from '@ssar-app/okta-authn-react';
interface CreateTemplateButtonProps {
  record: Job;
}

const showTermsAndConditions = (subCode: string): void => {
  const modal = Modal.confirm({
    icon: false,
    showFooterBtns: false,
    contentProps: { maxW: '80%' },
    header: 'Terms & Conditions',
    headerProps: { color: 'black', maxHeight: '100vh' },
    bodyProps: { pt: 0, pb: 6 },
    content: <></>,
  });
  const closeModal = modal.destroy;
  modal.update({
    content: (
      <TermsAndConditions
        type='ProgramSubmit'
        onAccept={(..._args) => {
          closeModal();
        }}
        onCancel={() => {
          closeModal();
        }}
        subCode={subCode}
      />
    ),
  });
};

export const CreateTemplateButton = (props: CreateTemplateButtonProps) => {
  const { record } = props;
  const history = useHistory();
  const { buildPath } = usePathContext();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [templateName, setTemplateName] = useState<string>('');
  const [templateDesc, setTemplateDesc] = useState<string>('');
  const [enableOk, setEnableOk] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const handleSubmit = () => {
    setIsLoadingData(true);
    createTemplate();
  };
  const createTemplate = async (): Promise<void> => {
    createTemplateByProgramById(
      record.programId,
      record.externalSystemRef,
      templateName,
      templateDesc,
    )
      .then((_result) => {
        history.push(buildPath('/dashboard?show=templates'));
        setIsModalVisible(false);
        modalUtils.showSuccessMessage('Successfully created template');
      })
      .catch((err) => {
        modalUtils.showError(err, 'Error while creating template.');
      })
      .finally(() => {
        setIsLoadingData(false);
      });
  };

  useEffect(() => {
    if (!isModalVisible) {
      setTemplateName('');
      setTemplateDesc('');
    }
  }, [isModalVisible]);

  return (
    <>
      <Modal
        isOpen={isModalVisible}
        header='Template Information'
        size='3xl'
        contentProps={{ maxW: '50%' }}
        onClose={() => setIsModalVisible(false)}
        bodyProps={{ pt: 5 }}
        showCancel
        cancelButtonProps={{
          size: 'sm',
          variant: 'outline',
        }}
        onOk={handleSubmit}
        closeOnOk={false}
        okButtonProps={{
          disabled: templateName.length < 3 || !enableOk || isLoadingData,
          size: 'sm',
          pl: 4,
          pr: 4,
        }}
        scrollBehavior='outside'
      >
        <Spin spinning={isLoadingData}>
          <FormControl isRequired={true}>
            <Grid templateRows='repeat(1,1fr)' mt={2}>
              <GridItem>
                <FormLabel>Template Name</FormLabel>
              </GridItem>
              <GridItem>
                <Tooltip placement='top' label='Name of the Template'>
                  <Input
                    onChange={(e) => {
                      setTemplateName(e.target.value);
                    }}
                    value={templateName}
                    placeholder='Template Name'
                    defaultValue={templateName}
                    spellCheck={false}
                  />
                </Tooltip>
                <FormHelperText fontSize={'xs'}>
                  Note: Template Name must be at least 3 characters
                </FormHelperText>
              </GridItem>
            </Grid>
          </FormControl>
          <FormControl>
            <Grid templateColumns='repeat(1,1fr)' mt={5}>
              <GridItem>
                <FormLabel>Template Description</FormLabel>
              </GridItem>
              <GridItem>
                <Textarea
                  placeholder={`Enter Template Description`}
                  value={templateDesc}
                  onChange={(e) => setTemplateDesc(e.target.value)}
                  size='sm'
                  spellCheck={false}
                />
              </GridItem>
            </Grid>
          </FormControl>
          <FormControl>
            <Checkbox
              onChange={(e) => {
                setEnableOk(e.target.checked);
              }}
              isChecked={enableOk}
              size='md'
              mt={5}
              pb={3}
            >
              I certify that I have an existing account relationship with all
              individual(s) for all Ascend Quest programs ordered using this
              Template and will use the consumer report information strictly for
              the FCRA permissible purpose of “account review” to assist in my
              decision to retain or modify the current account terms. Please
              read carefully, it is imperative that all Designates and End Users
              fully understand and adhere to our comprehensive{' '}
              <Button
                variant='link'
                onClick={() => showTermsAndConditions(record.subCode || '')}
                color={'#007bff'}
                _hover={{
                  borderBottom: 0,
                }}
              >
                Terms & Conditions
              </Button>
              . I understand that this Template will remain active until the
              Template is Archived.
            </Checkbox>
          </FormControl>
        </Spin>
      </Modal>
      <Tooltip placement='left' label='Create Template' hasArrow>
        <a
          href='/create-template'
          onClick={(e) => {
            e.preventDefault();
            setIsModalVisible(true);
          }}
        >
          <Icon as={AiOutlineSnippets} sx={{ fontSize: 'md', mb: -1 }} /> Create
          Template
        </a>
      </Tooltip>
    </>
  );
};
